import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {CalendarModale} from './CalendarModale';
import {
  ChangerDateDeLivraisonCommand,
  DateDeLivraisonChangeeEvent,
} from 'api-types/ffconnect';
import {useLivraisonsPossiblesContext, useSelectedLivraison} from 'ff-common';
import {Calendar} from './Calendar/Calendar';
import {useAfficherPanier} from '@/hooks/livraison/use-afficher-panier';
import {Box, Checkbox, Flex, Text} from '@chakra-ui/react';
import {State} from '@synako/halitrek';
import {ClientDesc, LivraisonDesc} from 'api-types/ffconnect-hal';
import {useNavigate} from 'react-router-dom';
import {buildRoute} from '@synako/enhanced-router';
import {routes} from 'ff-common';
import {useParams} from 'react-router-dom';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

export interface ChangerDateDeLivraisonModaleProps {
  submit?: (
    payload: ChangerDateDeLivraisonCommand,
  ) => Promise<State<DateDeLivraisonChangeeEvent> | undefined>;
  livraisonSelectionneeBO?: State<LivraisonDesc>;
  panierExisteBO?: boolean;
  livraisonsPossiblesBO?: State<LivraisonDesc>[];
  idClientBO?: string;
  titleModal?: string;
  clientBO?: State<ClientDesc>;
  redirectionAuPanier?: boolean;
  changerDateDeLivraison?: boolean;
}

export const ChangerDateDeLivraisonModale =
  NiceModal.create<ChangerDateDeLivraisonModaleProps>(
    ({
      submit,
      livraisonSelectionneeBO,
      panierExisteBO = false,
      livraisonsPossiblesBO,
      idClientBO,
      titleModal,
      clientBO,
      redirectionAuPanier = true,
      changerDateDeLivraison = false,
    }) => {
      const {t} = useTranslation();
      const modal = useModal();
      const afficherPanier = useAfficherPanier();
      const navigate = useNavigate();
      const params = useParams<{id_societe_commerciale: string}>();
      // État pour la checkbox "Différer aussi le panier"
      const [differerPanier, setDiffererPanier] = useState(false);

      const contexteOriginal = useLivraisonsPossiblesContext();
      const {livraisonSelectionnee: livraisonCtx, setLivraisonSelectionnee} =
        contexteOriginal;
      const livraisonInitiale = livraisonSelectionneeBO ?? livraisonCtx;

      const wrappedSetLivraisonSelectionnee = (
        newLivraison: State<LivraisonDesc>,
      ) => {
        setLivraisonSelectionnee(newLivraison);
      };

      const {selectedLivraison, onDateSelect, onCancel} = useSelectedLivraison(
        livraisonInitiale,
        wrappedSetLivraisonSelectionnee, // Utilise la fonction wrapper
        modal,
      );

      const onValidate = async () => {
        // Si nous sommes dans le backoffice (paramètres BO fournis)
        if (livraisonSelectionneeBO && idClientBO) {
          // Si la checkbox est cochée et qu'un panier existe, mettre à jour le panier
          if (differerPanier && submit) {
            try {
              const event = await submit({
                date_de_livraison:
                  selectedLivraison.data.tolede.date_de_livraison,
              });
              const panierId = event?.data?.data?.panier?.id;
              if (params.id_societe_commerciale && panierId) {
                navigate(
                  buildRoute(routes.client.backOfficePriseCommandePanierPdf, {
                    id_client: idClientBO,
                    panier_id: panierId,
                    id_societe_commerciale: params.id_societe_commerciale,
                  }),
                  {replace: true},
                );
                return;
              }
            } catch (error) {
              console.error('Erreur lors de la mise à jour de la date:', error);
            }
          }

          // Rediriger vers la nouvelle URL avec la date choisie
          if (params.id_societe_commerciale) {
            navigate(
              buildRoute(
                routes.client.backOfficePriseCommandeNouvelleCommande,
                {
                  id_client: idClientBO,
                  date_de_livraison:
                    selectedLivraison.data.tolede.date_de_livraison,
                  id_societe_commerciale: params.id_societe_commerciale,
                },
              ),
              {replace: true},
            );
          }
        } else {
          // Comportement normal pour l'interface client
          if (selectedLivraison.resource.uri !== livraisonCtx.uri && submit) {
            try {
              await submit({
                date_de_livraison:
                  selectedLivraison.data.tolede.date_de_livraison,
              });
            } catch (error) {
              console.error(
                'Erreur lors de la mise à jour de la date de livraison:',
                error,
              );
            }
          }
          if (changerDateDeLivraison) {
            // Met à jour la date de livraison globale
            setLivraisonSelectionnee(selectedLivraison);
          }
          if (redirectionAuPanier) {
            afficherPanier(selectedLivraison.data.tolede.date_de_livraison);
          }
        }
      };

      // Composant pour la checkbox pour différer aussi le panier dans le backoffice
      const CheckboxOption = () => (
        <Flex
          justifyContent='flex-start'
          pt='20px'
          width='100%'
        >
          <Checkbox
            isChecked={differerPanier}
            onChange={(e) => {
              setDiffererPanier(e.target.checked);
            }}
            borderColor='blue.main'
            colorScheme='blue'
          >
            <Text fontSize='sm'>
              {t('back_office.prise_de_commande.panier.differer_aussi_panier')}
            </Text>
          </Checkbox>
        </Flex>
      );

      return (
        <CalendarModale
          modal={modal}
          messageKey={titleModal ?? 'changement_panier'}
          onCancel={onCancel}
        >
          <Box
            borderRadius='8px'
            border='1px solid'
            borderColor='gray.light'
            height='100%'
          >
            <Calendar
              onCancel={onCancel}
              onDateSelect={onDateSelect}
              onValidate={onValidate}
              selectedLivraison={selectedLivraison}
              livraisonsPossiblesBO={livraisonsPossiblesBO}
              optionComponent={
                livraisonSelectionneeBO && panierExisteBO ? (
                  <CheckboxOption />
                ) : undefined
              }
              clientBO={clientBO}
              buttonText={t('modale_livraison.valider_date')}
            />
          </Box>
        </CalendarModale>
      );
    },
  );
